/* eslint-disable jsx-a11y/no-autofocus */
import { EnvironmentOutlined } from '@ant-design/icons'
import { Divider, Row, Typography } from 'antd'
import KSearch from 'app/components/Modules/KSearch'
import KList from 'app/components/Modules/List/KList'
import KListItem from 'app/components/Modules/List/KListItem'
import KCol from 'app/components/UI-Elements/Layout/KCol'
import { RecordType } from 'app/constants/GlobalTypes'
import { useBrowserRouterContext } from 'app/lib/contexts/KBrowserRouter'
import { loadGoogleScripts } from 'app/lib/hooks/loadGoogleScripts'
import { ToolBox } from 'app/lib/hooks/toolBox'
import { useAddress } from 'app/lib/hooks/useAddress'
import { useIndex } from 'app/lib/hooks/useApi'
import { attributeNames, mapProject } from 'app/lib/utils/delivery-address'
import { RecordTypeEnum } from 'app/queries'
import { debounce } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import PlacesAutocomplete from 'react-places-autocomplete'

import ProjectList from '../Project/List'
import useDeliveryAddressForm from '../Rental/hooks/useDeliveryAddressForm'

interface Props {
  rental: IRental
  onSuccess?: () => void
  showAvailabilityHint?: boolean
}

const DeliveryAddressSearch: React.FC<Props> = ({
  rental,
  onSuccess: onFinish,
  showAvailabilityHint = false
}) => {
  const { tRecordType } = ToolBox.useString()
  const { open: openAddressForm, close } = useDeliveryAddressForm(rental?.id?.toString())
  const {
    loading,
    execute,
    response: { data }
  } = useIndex<IProject>(RecordType.project)
  const [q, setQ] = useState<string>('')
  const { isLoaded } = loadGoogleScripts()
  const { fetchResults } = useAddress()
  const { removeLast } = useBrowserRouterContext()

  useEffect(() => {
    debouncedProjectSearch(q)
  }, [q])

  const debouncedProjectSearch = useCallback(
    debounce(
      (q) =>
        execute({
          q,
          per_page: q ? 3 : 10,
          filters: { with_name: true },
          sort: 'name'
        }),
      400
    ),
    []
  )

  const onProjectSelect = (project: IProject) => {
    openDrawer(mapProject(project))
  }

  const onAddressSelect = async (address: string) => {
    const result = await fetchResults(address, attributeNames)

    openDrawer({
      addressSummary: address,
      project: undefined, // Create new project
      projectId: undefined,
      ...result
    })
  }

  const onSuccess = () => {
    onFinish?.()
    removeLast()
    close()
  }

  const openDrawer = (member: IDeliveryAddress) => {
    openAddressForm({
      member,
      props: {
        formProps: { onSuccess },
        addressInputProps: { showAvailabilityHint },
        allowProjectSelection: false
      }
    })
  }

  return (
    <Row className="mobile-spacing">
      <KCol span={24} loading={!isLoaded}>
        <PlacesAutocomplete value={q} onChange={setQ}>
          {({ suggestions, loading: suggestionsLoading, getInputProps }) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { onBlur, ...inputProps } = getInputProps()
            return (
              <Row gutter={[0, 12]}>
                <KCol span={24}>
                  <KSearch
                    loading={loading || suggestionsLoading}
                    recordType={RecordTypeEnum.rentalDeliveryAddress}
                    autoFocus
                    showLabel
                    {...inputProps}
                  />
                </KCol>
                <If condition={rental?.project?.name}>
                  <KCol span={24} className="my-2">
                    <Row>
                      <KCol span={24} className="py-2">
                        <Typography.Text>
                          {tRecordType(RecordType.rental, 'current_delivery_address')}
                        </Typography.Text>
                      </KCol>
                      <KCol span={24}>
                        <Divider className="my-0" />
                        <ProjectList
                          collection={[rental?.project]}
                          props={{ loading, onItemClick: onProjectSelect }}
                        />
                        <Divider className="my-0" />
                      </KCol>
                    </Row>
                  </KCol>
                </If>
                <If condition={data.length}>
                  <KCol span={24} className="mb-2">
                    <Row>
                      <KCol span={24} className="py-2">
                        <Typography.Text>
                          {tRecordType(RecordType.project, 'project')}
                        </Typography.Text>
                      </KCol>
                      <KCol span={24}>
                        <Divider className="my-0" />
                        <ProjectList
                          collection={data}
                          props={{ loading, onItemClick: onProjectSelect }}
                        />
                        <Divider className="my-0" />
                      </KCol>
                    </Row>
                  </KCol>
                </If>
                <If condition={Boolean(q)}>
                  <KCol span={24}>
                    <Row>
                      <KCol span={24} className="py-2">
                        <span>{tRecordType(RecordType.rental, 'delivery_address')}</span>
                      </KCol>
                      <KCol span={24}>
                        <Divider className="my-0" />
                        <KList
                          pagination={false}
                          showTotal={false}
                          dataSource={[...suggestions]}
                          loading={suggestionsLoading}
                          renderItem={(suggestion) => (
                            <KListItem
                              onClick={() => onAddressSelect(suggestion.description)}
                              avatar={<EnvironmentOutlined className="font-size-22" />}
                              title={suggestion.description}
                            />
                          )}
                        />
                        <Divider className="my-0" />
                      </KCol>
                    </Row>
                  </KCol>
                </If>
              </Row>
            )
          }}
        </PlacesAutocomplete>
      </KCol>
    </Row>
  )
}

export default DeliveryAddressSearch
